/**
 * Account Settings API hooks — BOLT-026.
 *
 * React Query hooks for the four self-service profile endpoints + the
 * pending-email-change query. All endpoints live under /v1/users/me except
 * `verify-email-change` which is on /v1/auth.
 *
 * Endpoints (per BOLT-026 product-output.md Section 10):
 *   - PUT  /v1/users/me/password
 *   - PUT  /v1/users/me/profile
 *   - PUT  /v1/users/me/email
 *   - GET  /v1/users/me/pending-email-change
 *   - GET  /v1/users/me                       (current-user view)
 *
 * Cache invalidation strategy:
 *   - Password change: no cache change (no profile field updated).
 *   - Profile update: invalidates ['currentUser'] so AppShell display_name re-renders.
 *   - Email-change initiation: invalidates ['pending-email-change'] so the
 *     EmailSection banner appears.
 *   - Verification landing (in AccountSettingsPage): invalidates BOTH.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client.js';

// ---------------------------------------------------------------------------
// Response / payload shapes (mirrors API contracts in product-output.md §10)
// ---------------------------------------------------------------------------

export interface CurrentUser {
  id: string;
  email: string;
  display_name: string | null;
  phone: string | null;
  role: string;
  org_id: string | null;
}

export interface UpdatePasswordInput {
  current_password: string;
  new_password: string;
}

export interface UpdatePasswordResult {
  message: string;
}

export interface UpdateProfileInput {
  display_name?: string;
  phone?: string | null;
  /** ISO 3166-1 alpha-2 region hint for phone normalization (e.g. `"GB"`, `"AU"`). */
  phone_region?: string;
}

export interface UpdateProfileResult {
  id: string;
  display_name: string | null;
  email: string;
  phone: string | null;
}

export interface InitiateEmailChangeInput {
  new_email: string;
  password: string;
}

export interface InitiateEmailChangeResult {
  message: string;
}

export interface PendingEmailChange {
  new_email: string;
  expires_at: string;
}

// ---------------------------------------------------------------------------
// Query keys (centralized so invalidation is consistent across components)
// ---------------------------------------------------------------------------

export const accountQueryKeys = {
  currentUser: ['currentUser'] as const,
  pendingEmailChange: ['pending-email-change'] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Fetches the authenticated user's profile (display name, email, phone, role).
 * Used by AccountSettingsPage to populate forms and by EmailSection to display
 * the current email. Driven by the auth store — only enabled when a token
 * exists (avoids firing on the login page).
 */
export function useCurrentUser() {
  return useQuery<CurrentUser>({
    queryKey: accountQueryKeys.currentUser,
    queryFn: () => apiClient.get<CurrentUser>('/v1/users/me'),
  });
}

/**
 * Fetches the most recent unused, unexpired email-change verification token.
 * Returns null when no pending change exists. Driven by mount + manual
 * invalidation; no polling.
 */
export function usePendingEmailChange() {
  return useQuery<PendingEmailChange | null>({
    queryKey: accountQueryKeys.pendingEmailChange,
    queryFn: () => apiClient.get<PendingEmailChange | null>('/v1/users/me/pending-email-change'),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/**
 * Mutation for PUT /v1/users/me/password.
 * Server-side error codes mapped by the form to field-level errors:
 *   - 400 invalid_current_password → field error on current_password
 *   - 400 password_unchanged       → field error on new_password
 */
export function useUpdatePassword() {
  return useMutation<UpdatePasswordResult, Error, UpdatePasswordInput>({
    mutationFn: (body) => apiClient.put<UpdatePasswordResult>('/v1/users/me/password', { body }),
  });
}

/**
 * Mutation for PUT /v1/users/me/profile. Invalidates ['currentUser'] on
 * success so the AppShell user-profile display_name re-renders.
 */
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation<UpdateProfileResult, Error, UpdateProfileInput>({
    mutationFn: (body) => apiClient.put<UpdateProfileResult>('/v1/users/me/profile', { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueryKeys.currentUser });
    },
  });
}

/**
 * Mutation for PUT /v1/users/me/email — initiates an email change by sending
 * a verification email. Invalidates ['pending-email-change'] on success so
 * the EmailSection banner appears.
 *
 * Server error codes mapped by the dialog form:
 *   - 400 invalid_password → field error on password
 *   - 409 email_taken      → field error on new_email
 *   - 400 same_email       → field error on new_email
 */
export function useInitiateEmailChange() {
  const queryClient = useQueryClient();
  return useMutation<InitiateEmailChangeResult, Error, InitiateEmailChangeInput>({
    mutationFn: (body) => apiClient.put<InitiateEmailChangeResult>('/v1/users/me/email', { body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountQueryKeys.pendingEmailChange });
    },
  });
}
